import { createSelector } from 'reselect';

import type { LocaleValue } from '@transcend-io/internationalization';

import { matchLanguages } from '@main/core-ui';
import { DEFAULT_LOCALE } from '@main/internationalization';
import { selectRequiredPrivacyCenter } from '@main/privacy-center/src/containers/App/selectors';

import type { LocalizedRouterState } from './slice';

/**
 * The selector for the `LocalizedRouter` state store slice
 */
export const getLocalizedRouterSlice = (state: any): LocalizedRouterState =>
  state.LocalizedRouter;

/**
 * A mapping of locale -> true for quick checking of existence
 */
export const selectIsValidLocale = createSelector(
  selectRequiredPrivacyCenter,
  (privacyCenter) =>
    privacyCenter.locales.reduce(
      (acc, locale) => Object.assign(acc, { [locale]: true }),
      {} as { [k in string]: boolean },
    ),
);

/**
 * Select locale for this particular privacy center setting it to browser default if possible
 * else fall back to 'en' (global default)
 */
export const selectDefaultLocale = createSelector(
  selectRequiredPrivacyCenter,
  (privacyCenter) => {
    if (privacyCenter && privacyCenter.preferBrowserDefaultLocale) {
      const matchedLanguages = matchLanguages(
        navigator.languages,
        privacyCenter.locales,
      );
      if (matchedLanguages.length > 0) {
        return matchedLanguages[0] as LocaleValue;
      }
    }
    if (!privacyCenter.defaultLocale) {
      return DEFAULT_LOCALE;
    }
    return privacyCenter.defaultLocale as LocaleValue;
  },
);

/**
 * Translations for the current locale
 */
export const selectTranslations = createSelector(
  getLocalizedRouterSlice,
  (slice) => slice.translations,
);

/**
 * The language of the cached translations
 */
export const selectCachedLocale = createSelector(
  getLocalizedRouterSlice,
  (slice) => slice.locale,
);
